import { useId, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/cn'
import { ApiError } from '@/lib/apiClient'
import { minorUnits } from '@/lib/money'

import { useAdminMutation, useAdminQuery } from '../api'
import { useAdminContext } from '../session'
import type { AdminSetting, I18n } from '../types'
import { fromZonedInput, toZonedInput } from '../zonedInput'
import { I18nField, SwitchField } from './fields'

/**
 * Les réglages de l'édition.
 *
 * Chaque réglage s'enregistre **seul**, dès qu'on le change. C'est délibéré :
 * le soir J, ouvrir la commande à table ou relever le seuil de service se fait
 * en une seconde, entre deux services. Attendre qu'un formulaire de trente
 * champs soit valide pour appliquer une bascule serait absurde — et personne
 * ne le ferait sous pression.
 *
 * Les réglages viennent d'un **registre** côté serveur : cet écran ne connaît
 * aucun code en dur, il dessine ce qu'on lui envoie selon `value_type`. Un
 * réglage ajouté au catalogue apparaît ici sans qu'on touche au front.
 */
export function SettingsPanel({ eventId }: { eventId: string | undefined }) {
  const { t, i18n } = useTranslation()
  const [openGroup, setOpenGroup] = useState<string | null>(null)
  // Le dernier réglage écrit : un refus du serveur s'affiche sous LUI. En
  // haut du panneau, sur téléphone, il tombait hors de l'écran — on croyait
  // le numéro WhatsApp enregistré alors qu'il venait d'être refusé.
  const [lastCode, setLastCode] = useState<string | null>(null)

  const query = useAdminQuery<AdminSetting[]>(
    ['admin-settings', eventId],
    `/admin/events/${eventId}/settings`,
    eventId,
  )
  const save = useAdminMutation<AdminSetting[], { settings: { code: string; value: unknown }[] }>(
    'put',
    () => `/admin/events/${eventId}/settings`,
    eventId,
    [['admin-settings', eventId], ['public-event'], ['onsite-config']],
  )

  const settings = query.data ?? []

  // Un panneau qui s'efface ne dit rien. Il s'effaçait pourtant à la moindre
  // réponse vide : droits manquants, réseau coupé, chargement en cours — trois
  // causes très différentes, un seul écran blanc, et personne pour comprendre
  // pourquoi « Réglages » n'existe pas.
  if (query.isPending || query.isError || settings.length === 0) {
    return (
      <section className="rounded-lg border border-line-subtle bg-bg-elevated p-6">
        <h2 className="font-display text-heading-md text-content-primary">
          {t('admin.settings.title')}
        </h2>
        <p className="mt-2 text-sm text-content-secondary">
          {query.isPending
            ? t('admin.settings.loading')
            : query.error instanceof ApiError && query.error.status === 403
              ? t('admin.settings.forbidden')
              : query.isError
                ? t('admin.settings.failed')
                : t('admin.settings.emptyRegistry')}
        </p>
        {query.isError && !(query.error instanceof ApiError && query.error.status === 403) ? (
          <p className="mt-2 text-caption text-content-muted">
            {query.error instanceof Error ? query.error.message : ''}
          </p>
        ) : null}
      </section>
    )
  }

  const groups = orderGroups(settings.map((setting) => setting.group))
  // Un onglet mémorisé peut disparaître (groupe retiré du registre) : on
  // retombe alors sur le premier plutôt que sur un panneau vide.
  const active = openGroup && groups.includes(openGroup) ? openGroup : (groups[0] ?? null)

  function label(value: I18n, fallback: string) {
    return value[i18n.language] || value[Object.keys(value)[0] ?? ''] || fallback
  }

  function write(code: string, value: unknown) {
    setLastCode(code)
    save.mutate({ settings: [{ code, value }] })
  }

  const saveError = save.error instanceof ApiError ? save.error.message : null
  const failedSetting = saveError
    ? settings.find((setting) => setting.code === lastCode)
    : undefined

  return (
    <section className="rounded-lg border border-line-subtle bg-bg-elevated p-6">
      <h2 className="font-display text-heading-md text-content-primary">
        {t('admin.settings.title')}
      </h2>
      <p className="mt-1 text-sm text-content-secondary">{t('admin.settings.description')}</p>

      <div className="mt-5 flex flex-wrap gap-2">
        {groups.map((group) => (
          <button
            key={group}
            type="button"
            onClick={() => setOpenGroup(group)}
            aria-current={group === active}
            className={cn(
              'min-h-10 rounded-full border px-3 py-1.5 text-xs uppercase tracking-wide transition lg:min-h-0',
              group === active
                ? 'border-champagne-400/50 bg-bg-overlay text-content-primary'
                : 'border-line text-content-muted hover:text-content-secondary',
            )}
          >
            {/* Le code brut (« single », « onsite ») ne disait rien à qui
                n'a pas écrit le registre. Un groupe inconnu de la traduction
                garde son code : visible, plutôt qu'un onglet sans nom. */}
            {t(`admin.settings.groups.${group}`, { defaultValue: group })}
          </button>
        ))}
      </div>

      {/* Refus d'un réglage d'un autre onglet (ou introuvable) : il reste
          visible ici, en le nommant. */}
      {saveError && failedSetting?.group !== active ? (
        <p role="alert" className="mt-4 text-sm text-state-danger">
          {failedSetting ? `${label(failedSetting.label_i18n, failedSetting.code)} — ` : ''}
          {saveError}
        </p>
      ) : null}

      <div className="mt-5 grid gap-4">
        {settings
          .filter((setting) => setting.group === active)
          .map((setting) => (
            <div key={setting.code}>
              <SettingRow
                setting={setting}
                label={label(setting.label_i18n, setting.code)}
                help={label(setting.help_i18n, '')}
                optionLabel={(option) =>
                  option.value === ''
                    ? t('admin.settings.noneOption')
                    : label(option.label_i18n, option.value)
                }
                busy={save.isPending}
                onChange={(value) => write(setting.code, value)}
              />
              {saveError && failedSetting?.code === setting.code ? (
                <p role="alert" className="mt-1.5 px-1 text-sm text-state-danger">
                  {saveError}
                </p>
              ) : null}
            </div>
          ))}
      </div>
    </section>
  )
}

function SettingRow({
  setting,
  label,
  help,
  busy,
  optionLabel,
  onChange,
}: {
  setting: AdminSetting
  label: string
  help: string
  busy: boolean
  optionLabel: (option: SettingOption) => string
  onChange: (value: unknown) => void
}) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState<string | null>(null)

  // Un réglage encore à sa valeur d'usine mérite d'être signalé : on ne sait
  // pas, sinon, si « 30 € » est un choix ou un défaut jamais relu.
  const badge = setting.is_default ? (
    <span className="ml-2 rounded-full border border-line px-2 py-0.5 text-[10px] uppercase text-content-muted">
      {t('admin.settings.default')}
    </span>
  ) : null

  // Un choix fermé se choisit dans une liste, il ne se tape pas : saisi à la
  // main, « Exact » avec une majuscule ou un code de billet mal orthographié
  // passait l'écran et ne se découvrait qu'à la porte, le soir J.
  if (setting.options && setting.options.length > 0) {
    return (
      <OptionSetting
        setting={setting}
        options={setting.options}
        label={label}
        help={help}
        badge={badge}
        busy={busy}
        optionLabel={optionLabel}
        onChange={onChange}
      />
    )
  }

  if (setting.value_type === 'datetime') {
    return (
      <DatetimeSetting
        setting={setting}
        label={label}
        help={help}
        badge={badge}
        busy={busy}
        onChange={onChange}
      />
    )
  }

  if (setting.value_type === 'bool') {
    return (
      <div>
        <SwitchField
          label={label}
          hint={help}
          value={Boolean(setting.value)}
          onChange={(value) => onChange(value)}
        />
      </div>
    )
  }

  if (setting.value_type === 'i18n_text') {
    return (
      <div className="rounded-md border border-line-subtle px-4 py-3">
        <I18nField
          label={label}
          hint={help}
          value={(setting.value as I18n) ?? {}}
          onChange={(value) => onChange(value)}
        />
      </div>
    )
  }

  // `text` est, dans le registre, toujours une liste « un élément par ligne »
  // (les liens PayPal, par exemple). Un `<input>` d'une ligne rendait le
  // retour à la ligne impossible : on ne pouvait saisir qu'un seul lien.
  if (setting.value_type === 'text') {
    return (
      <div className="rounded-md border border-line-subtle px-4 py-3">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="min-w-0 text-caption uppercase tracking-wide text-content-muted">
            {label}
          </span>
          {badge}
        </div>
        <LinesEditor
          value={typeof setting.value === 'string' ? setting.value : ''}
          busy={busy}
          onChange={(value) => onChange(value)}
        />
        {help ? <p className="mt-1.5 text-xs text-content-muted">{help}</p> : null}
      </div>
    )
  }

  // `int` couvre deux choses très différentes : un compteur (secondes) et un
  // montant en centimes. On reconnaît le second à son suffixe — la même
  // convention que côté serveur — et on le saisit alors en euros.
  const isMoney = setting.code.endsWith('_minor')

  return (
    <div className="rounded-md border border-line-subtle px-4 py-3">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="min-w-0 text-caption uppercase tracking-wide text-content-muted">
          {label}
        </span>
        {badge}
      </div>
      <div className="mt-1.5 flex items-center gap-2">
        <input
          value={draft ?? formatValue(setting, isMoney)}
          inputMode={setting.value_type === 'int' ? 'decimal' : undefined}
          disabled={busy}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => {
            const raw = draft
            setDraft(null)
            if (raw === null) return
            onChange(parseValue(setting, raw, isMoney))
          }}
          className="w-full rounded-md border border-line bg-bg-base px-3 py-2.5 text-content-primary tabular-nums"
        />
        {isMoney ? <span className="shrink-0 text-sm text-content-muted">EUR</span> : null}
      </div>
      {help ? <p className="mt-1.5 text-xs text-content-muted">{help}</p> : null}
    </div>
  )
}

type SettingOption = NonNullable<AdminSetting['options']>[number]

/**
 * L'ordre des onglets suit le parcours d'une édition — ce que voit le public,
 * puis la vente, puis la soirée, puis le reste — et non l'alphabet, qui
 * mettait « compte à rebours » en tête et « pages » au milieu.
 */
const GROUP_ORDER = [
  'pages',
  'sales',
  'tables',
  'onsite',
  'countdown',
  'competition',
  'single',
  'policies',
  'contact',
  'screen',
  'project',
]

/** Groupes connus dans l'ordre ci-dessus ; un groupe inconnu va à la fin. */
function orderGroups(codes: string[]): string[] {
  const rank = (group: string) => {
    const index = GROUP_ORDER.indexOf(group)
    return index === -1 ? GROUP_ORDER.length : index
  }
  return [...new Set(codes)].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))
}

/** En-tête commun d'un réglage : libellé, pastille « par défaut ». */
function RowHeader({ label, badge, htmlFor }: { label: string; badge: ReactNode; htmlFor?: string }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
      <label
        htmlFor={htmlFor}
        className="min-w-0 text-caption uppercase tracking-wide text-content-muted"
      >
        {label}
      </label>
      {badge}
    </div>
  )
}

/**
 * Un réglage à choix fermé, enregistré dès qu'on choisit.
 *
 * La valeur enregistrée peut ne plus figurer parmi les choix — un type de
 * billet supprimé depuis, par exemple. On la montre alors telle quelle, marquée
 * « introuvable », plutôt que de laisser le menu afficher le premier choix
 * comme s'il était celui en vigueur.
 */
function OptionSetting({
  setting,
  options,
  label,
  help,
  badge,
  busy,
  optionLabel,
  onChange,
}: {
  setting: AdminSetting
  options: SettingOption[]
  label: string
  help: string
  badge: ReactNode
  busy: boolean
  optionLabel: (option: SettingOption) => string
  onChange: (value: unknown) => void
}) {
  const { t } = useTranslation()
  const id = useId()
  const current = typeof setting.value === 'string' ? setting.value : ''
  const known = options.some((option) => option.value === current)
  // Sans choix « aucun » prévu, une valeur vide doit quand même s'afficher
  // honnêtement : le menu montrerait sinon le premier choix.
  const showEmpty = !known && current === ''

  return (
    <div className="rounded-md border border-line-subtle px-4 py-3">
      <RowHeader label={label} badge={badge} htmlFor={id} />
      <select
        id={id}
        value={current}
        disabled={busy}
        onChange={(event) => {
          if (event.target.value !== current) onChange(event.target.value)
        }}
        className="mt-1.5 w-full min-w-0 rounded-md border border-line bg-bg-base px-3 py-2.5 text-content-primary"
      >
        {showEmpty ? (
          <option value="" disabled>
            {t('admin.settings.noneOption')}
          </option>
        ) : null}
        {!known && current !== '' ? (
          <option value={current} disabled>
            {t('admin.settings.unknownOption', { value: current })}
          </option>
        ) : null}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {optionLabel(option)}
          </option>
        ))}
      </select>
      {help ? <p className="mt-1.5 text-xs text-content-muted">{help}</p> : null}
    </div>
  )
}

/**
 * Une date-heure lue et saisie à l'heure de l'ÉDITION, pas de l'appareil.
 *
 * Le compte à rebours se règle souvent depuis un autre pays que celui de la
 * soirée : un champ à l'heure du téléphone décalait la cible d'une ou
 * plusieurs heures sans que rien ne le montre. Le fuseau utilisé est donc
 * affiché sous le champ.
 *
 * « Effacer » enregistre `null` : le registre dit qu'un réglage vide reprend
 * une valeur calculée (la date de début de l'édition pour le compte à
 * rebours). Vider le champ à la main ne suffisait pas sur tous les
 * navigateurs mobiles, dont le sélecteur n'a pas de bouton « vider ».
 */
function DatetimeSetting({
  setting,
  label,
  help,
  badge,
  busy,
  onChange,
}: {
  setting: AdminSetting
  label: string
  help: string
  badge: ReactNode
  busy: boolean
  onChange: (value: unknown) => void
}) {
  const { t } = useTranslation()
  const { event } = useAdminContext()
  const id = useId()
  const timeZone = event?.timezone || 'Europe/Berlin'
  const saved = toZonedInput(typeof setting.value === 'string' ? setting.value : null, timeZone)
  const [draft, setDraft] = useState<string | null>(null)
  const shown = draft ?? saved

  // Enregistré en quittant le champ, comme les autres saisies : le sélecteur
  // natif émet un changement à chaque molette tournée, et autant d'écritures
  // ne servent à rien.
  function commit() {
    const raw = draft
    setDraft(null)
    if (raw === null || raw === saved) return
    if (raw === '') return onChange(null)
    const iso = fromZonedInput(raw, timeZone)
    if (iso) onChange(iso)
  }

  return (
    <div className="rounded-md border border-line-subtle px-4 py-3">
      <RowHeader label={label} badge={badge} htmlFor={id} />
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <input
          id={id}
          type="datetime-local"
          value={shown}
          disabled={busy}
          onChange={(changeEvent) => setDraft(changeEvent.target.value)}
          onBlur={commit}
          className="min-w-0 flex-1 basis-48 rounded-md border border-line bg-bg-base px-3 py-2.5 text-content-primary tabular-nums"
        />
        <button
          type="button"
          disabled={busy || (saved === '' && !draft)}
          onClick={() => {
            setDraft(null)
            if (setting.value !== null && setting.value !== undefined) onChange(null)
          }}
          className="shrink-0 rounded-md border border-line px-3 py-2.5 text-xs text-content-secondary transition hover:border-state-danger/60 hover:text-state-danger disabled:opacity-50"
        >
          {t('admin.settings.datetime.clear')}
        </button>
      </div>
      <p className="mt-1.5 text-xs text-content-muted">
        {t('admin.settings.datetime.timezone', { timezone: timeZone })}
        {saved === '' ? ` ${t('admin.settings.datetime.empty')}` : null}
      </p>
      {help ? <p className="mt-1 text-xs text-content-muted">{help}</p> : null}
    </div>
  )
}

/**
 * Une liste éditable ligne par ligne, stockée comme texte « un par ligne ».
 *
 * Chaque ligne s'enregistre en quittant le champ ; ajouter ou retirer une
 * ligne enregistre aussitôt. Une ligne vide n'est jamais envoyée : le serveur
 * l'ignorerait de toute façon, autant ne pas la montrer comme un élément.
 */
function LinesEditor({
  value,
  busy,
  onChange,
}: {
  value: string
  busy: boolean
  onChange: (value: string) => void
}) {
  const { t } = useTranslation()
  const saved = value.split(/\r?\n/).map((line) => line.trim()).filter(Boolean)
  const [lines, setLines] = useState<string[] | null>(null)
  const current = lines ?? saved

  function commit(next: string[]) {
    const cleaned = next.map((line) => line.trim()).filter(Boolean)
    setLines(null)
    if (cleaned.join('\n') !== saved.join('\n')) onChange(cleaned.join('\n'))
  }

  return (
    <div className="mt-2 grid gap-2">
      {current.map((line, index) => (
        <div key={index} className="flex items-center gap-2">
          <span className="w-6 shrink-0 text-right text-xs tabular-nums text-content-muted">
            {index + 1}.
          </span>
          <input
            value={line}
            disabled={busy}
            onChange={(event) => {
              const next = [...current]
              next[index] = event.target.value
              setLines(next)
            }}
            onBlur={() => commit(current)}
            className="w-full min-w-0 rounded-md border border-line bg-bg-base px-3 py-2 text-sm text-content-primary"
          />
          <button
            type="button"
            disabled={busy}
            onClick={() => commit(current.filter((_, position) => position !== index))}
            aria-label={t('admin.settings.removeLine')}
            className="shrink-0 rounded-md border border-line px-2.5 py-2 text-xs text-content-muted transition hover:border-state-danger/60 hover:text-state-danger"
          >
            ✕
          </button>
        </div>
      ))}
      {current.length === 0 ? (
        <p className="text-xs text-content-muted">{t('admin.settings.noLines')}</p>
      ) : null}
      <div>
        <button
          type="button"
          disabled={busy || current.some((line) => !line.trim())}
          onClick={() => setLines([...current, ''])}
          className="rounded-md border border-line px-3 py-1.5 text-xs text-content-secondary transition hover:border-champagne-400/50 hover:text-content-primary disabled:opacity-50"
        >
          + {t('admin.settings.addLine')}
        </button>
      </div>
    </div>
  )
}

function formatValue(setting: AdminSetting, isMoney: boolean): string {
  if (setting.value === null || setting.value === undefined) return ''
  if (isMoney && typeof setting.value === 'number') {
    return (setting.value / 10 ** minorUnits('EUR')).toFixed(minorUnits('EUR'))
  }
  if (typeof setting.value === 'object') return JSON.stringify(setting.value)
  return String(setting.value)
}

function parseValue(setting: AdminSetting, raw: string, isMoney: boolean): unknown {
  const trimmed = raw.trim()
  // Un champ vidé veut dire « pas de valeur » : la date du compte à rebours
  // dit « laisser vide… », et l'envoyer vide échouait (date illisible, ou 0).
  if (trimmed === '' && ['int', 'datetime', 'media'].includes(setting.value_type)) return null
  if (setting.value_type === 'int') {
    const parsed = Number(trimmed.replace(',', '.'))
    if (!Number.isFinite(parsed)) return setting.value
    // `Math.round` obligatoire : `30.00 * 100` peut valoir 2999,999… en
    // virgule flottante, et un seuil à 29,99 € n'est pas celui qu'on a saisi.
    return isMoney ? Math.round(parsed * 10 ** minorUnits('EUR')) : Math.round(parsed)
  }
  if (setting.value_type === 'json') {
    try {
      return JSON.parse(trimmed)
    } catch {
      // Un JSON invalide ne doit pas écraser une configuration valide.
      return setting.value
    }
  }
  return trimmed
}
